import { Info } from "lucide-react";
import { type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Amount } from "@/components/amount";
import { formatStatusLabel } from "@/lib/utils";

// The info-icon button next to a "Total" balance that reveals
// Pending/Frozen/Locked/Updated without needing four more table columns —
// shared by the merchant detail page's Ledger and Deposit wallets tabs and
// the global Ledger list. A plain structural interface rather than any one
// page's own item type, since all three already carry exactly these fields.

export interface BalanceBreakdownItem {
  assetSymbol: string;
  assetNetwork: string;
  balancePending: string;
  balanceFrozen: string;
  balanceLocked: string;
  balanceUpdatedAt: string | null;
}

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </dd>
    </div>
  );
}

export function BalanceBreakdownDialog({ account }: { account: BalanceBreakdownItem }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="ghost" size="icon" aria-label="Balance breakdown">
          <Info className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Balance breakdown</DialogTitle>
          <DialogDescription>
            {account.assetSymbol} · {formatStatusLabel(account.assetNetwork)}
          </DialogDescription>
        </DialogHeader>
        <dl className="space-y-2 text-sm">
          <Row label="Pending" value={<Amount native={account.balancePending} symbol={account.assetSymbol} />} />
          <Row label="Frozen" value={<Amount native={account.balanceFrozen} symbol={account.assetSymbol} />} />
          <Row label="Locked" value={<Amount native={account.balanceLocked} symbol={account.assetSymbol} />} />
          <Row label="Updated" value={formatTimestamp(account.balanceUpdatedAt)} />
        </dl>
      </DialogContent>
    </Dialog>
  );
}
